import { validateNumeric2DArray, validateArray, validateShapesMatch, validateFitted, validatePositiveInteger } from '../utils/validation.js';
import { euclideanDistance } from '../utils/matrix.js';
import { mode } from '../utils/stats.js';

export class KNeighborsClassifier {
  constructor(options = {}) {
    this.k = options.k || 5;
    validatePositiveInteger(this.k, 'k');

    this.weights = options.weights || 'uniform';
    this.metric = options.metric || 'euclidean';

    this.XTrain = null;
    this.yTrain = null;
    this.classes = null;
    this.isFitted = false;
  }

  fit(X, y) {
    if (!Array.isArray(X[0])) {
      X = X.map(val => [val]);
    }

    validateNumeric2DArray(X, 'X');
    validateArray(y, 'y');
    validateShapesMatch(X, y);

    this.XTrain = X.map(row => [...row]);
    this.yTrain = [...y];
    this.classes = Array.from(new Set(y)).sort();

    this.isFitted = true;
    return this;
  }

  predict(X) {
    validateFitted(this.isFitted, 'KNeighborsClassifier');

    if (!Array.isArray(X[0])) {
      X = X.map(val => [val]);
    }

    validateNumeric2DArray(X, 'X');

    return X.map(testPoint => {
      const distances = this.XTrain.map((trainPoint, i) => ({
        distance: euclideanDistance(testPoint, trainPoint),
        label: this.yTrain[i]
      }));

      distances.sort((a, b) => a.distance - b.distance);

      const kNearest = distances.slice(0, this.k);

      if (this.weights === 'uniform') {
        const labels = kNearest.map(neighbor => neighbor.label);
        return mode(labels);
      } else if (this.weights === 'distance') {
        const weightedVotes = {};

        for (const neighbor of kNearest) {
          const weight = neighbor.distance === 0 ? 1e10 : 1 / neighbor.distance;
          weightedVotes[neighbor.label] = (weightedVotes[neighbor.label] || 0) + weight;
        }

        return Object.keys(weightedVotes).reduce((a, b) =>
          weightedVotes[a] > weightedVotes[b] ? a : b
        );
      }
    });
  }

  predictProba(X) {
    validateFitted(this.isFitted, 'KNeighborsClassifier');

    if (!Array.isArray(X[0])) {
      X = X.map(val => [val]);
    }

    validateNumeric2DArray(X, 'X');

    return X.map(testPoint => {
      const distances = this.XTrain.map((trainPoint, i) => ({
        distance: euclideanDistance(testPoint, trainPoint),
        label: this.yTrain[i]
      }));

      distances.sort((a, b) => a.distance - b.distance);

      const kNearest = distances.slice(0, this.k);

      const probabilities = {};
      this.classes.forEach(c => probabilities[c] = 0);

      if (this.weights === 'uniform') {
        for (const neighbor of kNearest) {
          probabilities[neighbor.label] += 1 / this.k;
        }
      } else {
        let totalWeight = 0;
        for (const neighbor of kNearest) {
          const weight = neighbor.distance === 0 ? 1e10 : 1 / neighbor.distance;
          probabilities[neighbor.label] += weight;
          totalWeight += weight;
        }

        for (const c of this.classes) {
          probabilities[c] /= totalWeight;
        }
      }

      return this.classes.map(c => probabilities[c]);
    });
  }

  score(X, y) {
    validateFitted(this.isFitted, 'KNeighborsClassifier');

    const predictions = this.predict(X);
    const correct = predictions.filter((pred, i) => pred === y[i]).length;
    return correct / y.length;
  }
}
